import PwaNetworkDiagnosticsController from "./pwa/network_diagnostics_controller"
import db from "../field_db"
import { logEvent } from "../field_logger"
import { logSpeedEvent, SPEED_TEST_COMPLETED } from "../lib/field_speed_events"
import { currentLevel, evidenceSnapshot, LEVEL_OFFLINE, LEVEL_DEGRADED, LEVEL_ONLINE } from "../field_network_health"
import { classifyQuality, isGoodOrFair } from "../pwa_network_quality"

// Field app network diagnostics -- extends shared base with:
//   - Offline cache stats from IndexedDB (cached_responses, pending_responses)
//   - Storage usage
//   - field_logger integration
//   - Connectivity-level readout (ADR-0110, Card 1) -- read-only, observe-only
//   - Manual-offline "safe to go online" verdict (card #4428)
//
// Extra targets: cachedCount, lastSynced, pendingCount, storageUsed,
// storageQuota, healthLevel, healthEvidence, verdict, verdictLabel, verdictDetail

const LEVEL_PRESENTATION = {
  [LEVEL_OFFLINE]:  { label: "Offline",  className: "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700" },
  [LEVEL_DEGRADED]: { label: "Degraded", className: "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700" },
  [LEVEL_ONLINE]:   { label: "Online",   className: "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700" }
}

export default class extends PwaNetworkDiagnosticsController {
  static targets = [
    ...PwaNetworkDiagnosticsController.targets,
    "cachedCount", "lastSynced", "pendingCount", "storageUsed", "storageQuota",
    "healthLevel", "healthEvidence", "evidenceRowTemplate",
    "verdict", "verdictLabel", "verdictDetail"
  ]

  connect() {
    super.connect()
    this._renderHealth()
    this._healthChangedHandler = () => this._renderHealth()
    window.addEventListener("field:health-changed", this._healthChangedHandler)
  }

  disconnect() {
    super.disconnect()
    window.removeEventListener("field:health-changed", this._healthChangedHandler)
  }

  async _loadCacheStats() {
    try {
      const cached  = await db.cached_responses.count()
      const pending = await db.pending_responses.count()

      if (this.hasCachedCountTarget)  this.cachedCountTarget.textContent  = cached
      if (this.hasPendingCountTarget) this.pendingCountTarget.textContent = pending

      const lastSync = localStorage.getItem("field:last-prefetch")
      if (this.hasLastSyncedTarget) {
        this.lastSyncedTarget.textContent = lastSync
          ? new Date(lastSync).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })
          : "Never"
      }
    } catch {
      // IndexedDB unavailable
    }

    try {
      const estimate = await navigator.storage?.estimate()
      if (estimate && this.hasStorageUsedTarget) {
        this.storageUsedTarget.textContent = this._formatBytes(estimate.usage || 0)
        if (this.hasStorageQuotaTarget) {
          this.storageQuotaTarget.textContent = this._formatBytes(estimate.quota || 0)
        }
      }
    } catch {
      // Storage API unavailable
    }
  }

  _extraDiagnostics() {
    const lastSync = localStorage.getItem("field:last-prefetch") || "Never"
    return [`Last sync: ${lastSync}`]
  }

  async runTest() {
    await super.runTest()
    const successful = this._lastResults.filter(r => r.ok)
    const avg = successful.length
      ? Math.round(successful.reduce((s, r) => s + r.ms, 0) / successful.length)
      : null
    const loss = Math.round(((this._lastResults.length - successful.length) / this._lastResults.length) * 100)
    logSpeedEvent(SPEED_TEST_COMPLETED, {
      avgMs: avg,
      packetLossPercent: loss,
      pings: this._lastResults.length
    }).catch(() => {})

    this._renderVerdict(avg, loss)
  }

  // -- manual-offline "safe to go online" verdict (card #4428) --
  //
  // Only meaningful while the tasker is deliberately offline: the SW's
  // forcedOffline gate now lets this page's own ping (and only this ping)
  // through to the real network (field_service_worker.js), so this test's
  // avg/loss is a genuine reading even though the app stays gated. Reuses
  // the SAME classifier as the quality label above and field_network_health.js
  // (ADR-0110) -- no second classifier invented here.
  _renderVerdict(avgMs, lossPercent) {
    if (!this.hasVerdictTarget) return

    const manualOffline = localStorage.getItem("field:offline_mode") === "manual" &&
      localStorage.getItem("field:online") === "false"
    if (!manualOffline) {
      this.verdictTarget.classList.add("hidden")
      return
    }

    const safe = isGoodOrFair(classifyQuality(avgMs, lossPercent))

    // Pending count target is populated by _loadCacheStats() at connect --
    // read it rather than re-querying IndexedDB. "--" means it hasn't
    // resolved yet, so the verdict detail says nothing about the queue.
    const pendingRaw   = this.hasPendingCountTarget ? this.pendingCountTarget.textContent.trim() : ""
    const pendingCount = /^\d+$/.test(pendingRaw) ? Number(pendingRaw) : null

    this.verdictTarget.classList.remove("hidden")

    if (this.hasVerdictLabelTarget) {
      this.verdictLabelTarget.textContent = safe ? "Safe to go online" : "Wait"
      this.verdictLabelTarget.className = safe
        ? "text-sm font-semibold text-emerald-700"
        : "text-sm font-semibold text-amber-800"
    }

    if (this.hasVerdictDetailTarget) {
      const pendingText = pendingCount == null
        ? ""
        : pendingCount > 0
          ? ` ${pendingCount} pending upload${pendingCount === 1 ? "" : "s"} will sync automatically.`
          : " No pending uploads waiting."
      this.verdictDetailTarget.textContent = safe
        ? `Connection looks good.${pendingText}`
        : `Connection is still poor -- going online now may stall.${pendingText}`
    }
  }

  _log(message) {
    logEvent("network", message).catch(() => {})
  }

  // -- connectivity-level readout (ADR-0110, Card 1) --

  _renderHealth() {
    const presentation = LEVEL_PRESENTATION[currentLevel()] || LEVEL_PRESENTATION[LEVEL_ONLINE]

    if (this.hasHealthLevelTarget) {
      this.healthLevelTarget.textContent = presentation.label
      this.healthLevelTarget.className   = presentation.className
    }

    if (!this.hasHealthEvidenceTarget) return

    const samples = evidenceSnapshot().slice(-10).reverse()
    this.healthEvidenceTarget.replaceChildren()

    if (samples.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-xs text-slate-400 px-4 py-3"
      empty.textContent = "No evidence recorded yet this session"
      this.healthEvidenceTarget.appendChild(empty)
      return
    }

    for (const sample of samples) {
      this.healthEvidenceTarget.appendChild(this._buildEvidenceRow(sample))
    }
  }

  _buildEvidenceRow(sample) {
    const fragment  = this.evidenceRowTemplateTarget.content.cloneNode(true)
    const kindEl    = fragment.querySelector("[data-role='kind']")
    const durationEl = fragment.querySelector("[data-role='duration']")
    const outcomeEl = fragment.querySelector("[data-role='outcome']")

    kindEl.textContent     = sample.kind || "unknown"
    durationEl.textContent = sample.durationMs != null ? `${sample.durationMs}ms` : "no response"
    outcomeEl.textContent  = sample.outcome
    outcomeEl.className    = sample.outcome === "ok"
      ? "text-xs font-medium text-emerald-600"
      : "text-xs font-medium text-red-600"

    return fragment
  }
}
